import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CreditCardSetupInitiatorDialog } from './CreditCardSetupInitiatorDialog';
import { useDisclosure, useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import type { RegistryErrorResultFragment } from '@graphql/generated';
import {
  EventUserStatus,
  RegistryPayoutAccountWithdrawalStatus,
  ShippingAddressFragment,
  useCreateRegistryPayoutAccountMutation,
  useGetPayoutAccountInfoLazyQuery,
} from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import { getLocalStorage } from '@shared/core';
import { normalizeGraphError, useTranslateGraphError, errorFromGraphError } from '@shared/utils/graphError';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import { PayoutAccountInfo, Provider } from './CreditCardSetUp.context';
import { withWindow } from '@shared/utils/withWindow';
import { PayoutDialog } from './PayoutDialog/PayoutDialog';
import { usePayoutDialogController } from './PayoutDialog/PayoutDialog.controller';
import { TransferToBankDialogView } from '@shared/components/TransferToBankDialog';
import { useAdminRegistryTelemetry } from '../../AdminRegistry.telemetry';
import { isAddressValid, isUSAddress } from '@apps/registry/common/utils/shippingAddressValidations';
import { usePreviousValue } from '@shared/utils/hooks/usePreviousValue';
import { canWithdrawFunds, hasActiveStripeAccount } from './CreditCardSetup.utils';
import { useCreditCardSetupTelemetry } from './CreditCardSetUp.telemetry';
import { addError } from '@shared/utils/logger';

const CREDIT_CARD_SETUP_STARTED = 'credit:card:setup';
export const REFUND_IN_PROGRESS = 'credit:card:setup:refund';

// we're playing with this until we figure out the right amount of attempts and time.
const MAX_ATTEMPTS = 4;

export type CreditCardSetupProviderProps = Readonly<{
  children: React.ReactNode;
  eventId: string;
  registryShippingAddress: Readonly<ShippingAddressFragment> | null;
  handleInvalidShippingAddress?: () => void;
}>;

const CreditCardSetupProvider: React.FC<CreditCardSetupProviderProps> = ({ children, eventId, registryShippingAddress, handleInvalidShippingAddress }) => {
  const openWindow = useWindowFunction('open');
  const {
    currentUser: { activeEventUser },
  } = useAuth();
  const { toast } = useToast();
  const { translateGraphErrorCodeOrUnknown } = useTranslateGraphError();
  const telemetry = useCreditCardSetupTelemetry();
  const adminRegistryTelemetry = useAdminRegistryTelemetry();
  const [payoutAccountInfo, setPayoutAccountInfo] = useState<PayoutAccountInfo | null>(null);
  const localStorage = getLocalStorage();
  const [attempts, setAttempts] = useState(0);
  const maxAttemptsReached = attempts >= MAX_ATTEMPTS;
  const isValidUSAddress = useMemo(() => isAddressValid(registryShippingAddress) && isUSAddress(registryShippingAddress), [registryShippingAddress]);
  const shouldSkipAddressValidation = useMemo(() => {
    // Skip address validation for accounts with active Stripe connection (onboarding, pending, or readyToUse)
    // User already went through onboarding flow, so no need to re-validate address
    // Rejected accounts require restarting onboarding (with address validation)
    if (!payoutAccountInfo) return false;
    return hasActiveStripeAccount(payoutAccountInfo.status);
  }, [payoutAccountInfo]);
  const handleMutationError = useCallback(
    (result: RegistryErrorResultFragment, label: string) => {
      const normalized = normalizeGraphError(result);
      const errorMessage = translateGraphErrorCodeOrUnknown(normalized?.code);
      toast(errorMessage, { icon: <WarningTriangle />, duration: 10000 });
      telemetry.trackError(label, errorFromGraphError(result), {
        eventId,
        code: normalized?.code,
        nativeMessage: normalized?.nativeMessage,
      });
    },
    [eventId, toast, translateGraphErrorCodeOrUnknown, telemetry],
  );

  const [getPayoutInfo, { loading: fetchingPayoutInfo }] = useGetPayoutAccountInfoLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const payoutAccount = data.getRegistryEventData?.payoutAccount;
      setPayoutAccountInfo((payoutAccount as PayoutAccountInfo) || null);
    },
  });
  const [createPayoutAccount, { loading: initPayoutAccountLoading }] = useCreateRegistryPayoutAccountMutation({
    onCompleted: data => {
      const createRegistryPayoutAccount = data.createRegistryPayoutAccount;
      if (createRegistryPayoutAccount.__typename === 'RegistryPayoutAccount') {
        localStorage.setItem(CREDIT_CARD_SETUP_STARTED, 'true');
        const url = createRegistryPayoutAccount.onboardingUrl ?? '';
        if (url) openWindow(url, '_self');
      } else if (createRegistryPayoutAccount.__typename === 'RegistryErrorResult') {
        handleMutationError(createRegistryPayoutAccount, 'CreateRegistryPayoutAccount');
      }
    },
    onError: error => {
      addError(error, { context: 'CreditCardSetup.createPayoutAccount', eventId });
      telemetry.trackError('CreateRegistryPayoutAccount', error, { eventId });
    },
  });

  const [createPayoutAccountKYCLater, { loading: initKYCLaterLoading }] = useCreateRegistryPayoutAccountMutation({
    onCompleted: data => {
      const result = data.createRegistryPayoutAccount;
      if (result.__typename === 'RegistryPayoutAccount') {
        getPayoutInfo({ variables: { eventId } });
      } else if (result.__typename === 'RegistryErrorResult') {
        handleMutationError(result, 'CreateRegistryPayoutAccountKYCLater');
      }
    },
    onError: error => {
      addError(error, { context: 'CreditCardSetup.createPayoutAccountKYCLater', eventId });
      telemetry.trackError('CreateRegistryPayoutAccountKYCLater', error, { eventId });
    },
  });

  const initKYCOnboarding = useCallback(() => createPayoutAccount({ variables: { eventId } }), [eventId, createPayoutAccount]);
  const initKYCLaterOnboarding = useCallback(
    async (firstName: string, lastName: string): Promise<boolean> => {
      try {
        const result = await createPayoutAccountKYCLater({
          variables: { eventId, payload: { firstName, lastName } },
        });
        return result.data?.createRegistryPayoutAccount.__typename === 'RegistryPayoutAccount';
      } catch (error) {
        addError(error, { context: 'CreditCardSetup.initKYCLaterOnboarding', eventId });
        return false;
      }
    },
    [eventId, createPayoutAccountKYCLater],
  );
  const initKYCOnboardingFromRefund = useCallback(
    (returnRoute: string) => {
      localStorage.setItem(REFUND_IN_PROGRESS, 'true');
      createPayoutAccount({ variables: { eventId, returnRoute } });
    },
    [eventId, localStorage, createPayoutAccount],
  );
  const fetchPayoutInfo = useCallback(() => getPayoutInfo({ variables: { eventId } }), [eventId, getPayoutInfo]);
  const [setupCreditCard, setSetupCreditCard] = useManagedSearchParam('setupCreditCard');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenTransferToBankDialog, onOpen: onOpenTransferToBankDialog, onClose: onCloseTransferToBankDialog } = useDisclosure();

  const isBankConnected = !!payoutAccountInfo && canWithdrawFunds(payoutAccountInfo.status);

  const {
    withdrawBalance,
    loadingPayoutRequest,
    payoutAccountAllowed,
    isOpenPayoutDialog,
    onOpenPayoutDialog,
    onClosePayoutDialog,
    resetPayoutStatus,
    payoutStatus,
    accountNumber,
  } = usePayoutDialogController(fetchPayoutInfo, payoutAccountInfo, eventId as string);

  const handleTransferToBankDialogClose = useCallback(() => {
    onCloseTransferToBankDialog();
    if (isBankConnected) {
      resetPayoutStatus();
    }
  }, [isBankConnected, resetPayoutStatus, onCloseTransferToBankDialog]);

  const handleTransferToBankConnectBank = useCallback(() => {
    onCloseTransferToBankDialog();
    const redirectUrl = payoutAccountInfo?.onboardingUrl || payoutAccountInfo?.serviceDashboardUrl;
    if (redirectUrl) {
      openWindow(redirectUrl, '_self');
    } else {
      initKYCOnboarding();
    }
  }, [initKYCOnboarding, onCloseTransferToBankDialog, payoutAccountInfo?.onboardingUrl, payoutAccountInfo?.serviceDashboardUrl, openWindow]);

  // const dialogRef = useOuterClick(onToggle, isOpen);
  // State will be handle by a query param, if this query param is present the modal will close and open.
  useEffect(() => {
    if (setupCreditCard) {
      onOpen();
    } else {
      onClose();
    }
  }, [onOpen, setupCreditCard, onClose]);

  useEffect(() => {
    if (localStorage.getItem(CREDIT_CARD_SETUP_STARTED)) {
      if (!localStorage.getItem(REFUND_IN_PROGRESS)) {
        setSetupCreditCard('true');
      }
      localStorage.setItem(CREDIT_CARD_SETUP_STARTED, '');
    }
  }, [localStorage, fetchingPayoutInfo, payoutAccountInfo, setSetupCreditCard]);

  // we don't clear out historical failures, so we only emphasize the last withdrawal
  const hasFailedWithdrawals = useMemo(() => payoutAccountInfo?.withdrawals.at(-1)?.status === RegistryPayoutAccountWithdrawalStatus.failed, [payoutAccountInfo]);
  const hasPendingWithdrawals = useMemo(
    () => payoutAccountInfo?.withdrawals.some(withdrawal => withdrawal.status === RegistryPayoutAccountWithdrawalStatus.pending) ?? false,
    [payoutAccountInfo],
  );

  const prevHasPendingWithdrawals = usePreviousValue(hasPendingWithdrawals);
  const [hasDoneWithdrawal, setHasDoneWithdrawal] = useState(false);
  useEffect(() => {
    // a withdrawal is pending, so clear done withdrawal
    if (hasPendingWithdrawals) {
      setHasDoneWithdrawal(false);
      return;
    }
    // previous withdrawal has been completed without error
    if (!hasPendingWithdrawals && prevHasPendingWithdrawals && !hasFailedWithdrawals) {
      setHasDoneWithdrawal(true);
      return;
    }
  }, [hasFailedWithdrawals, prevHasPendingWithdrawals, hasPendingWithdrawals, setHasDoneWithdrawal]);
  useEffect(() => {
    return withWindow(
      global => {
        let timeout: number;
        if (payoutAccountInfo && (payoutAccountInfo.status === 'pending' || hasPendingWithdrawals)) {
          if (attempts <= MAX_ATTEMPTS) {
            timeout = global.setTimeout(() => {
              setAttempts(prevValue => prevValue + 1);
              fetchPayoutInfo();
            }, 10_000);
          }
        }

        return () => {
          if (timeout) {
            global.clearTimeout(timeout);
          }
        };
      },
      () => {},
    );
  }, [payoutAccountInfo, fetchPayoutInfo, eventId, attempts, setAttempts, hasPendingWithdrawals]);

  useEffect(() => {
    fetchPayoutInfo();
  }, [fetchPayoutInfo, eventId]);
  return (
    <Provider
      value={{
        hasDoneWithdrawal,
        hasFailedWithdrawals,
        hasPendingWithdrawals,
        setSetupCreditCard,
        fetchingPayoutInfo,
        payoutAccountInfo,
        maxAttemptsReached,
        initKYCOnboarding,
        fetchPayoutInfo,
        onOpenPayoutDialog,
        onOpenTransferToBankDialog,
        initKYCOnboardingFromRefund,
        initKYCLaterOnboarding,
        initPayoutAccountLoading,
        initKYCLaterLoading,
      }}
    >
      {children}
      <CreditCardSetupInitiatorDialog
        payoutAccountInfo={payoutAccountInfo}
        isOwner={Boolean(activeEventUser?.status === EventUserStatus.owner)}
        onClose={() => setSetupCreditCard(null)}
        isOpen={isOpen}
        initKYCOnboarding={initKYCOnboarding}
        initPayoutAccountLoading={initPayoutAccountLoading}
        initKYCLaterOnboarding={initKYCLaterOnboarding}
        initKYCLaterLoading={initKYCLaterLoading}
        maxAttemptsReached={maxAttemptsReached}
        isValidUSAddress={isValidUSAddress}
        shouldSkipAddressValidation={shouldSkipAddressValidation}
        handleInvalidShippingAddress={handleInvalidShippingAddress}
      />
      <TransferToBankDialogView
        isOpen={isOpenTransferToBankDialog}
        onClose={handleTransferToBankDialogClose}
        availableBalance={payoutAccountInfo?.balance?.available ?? undefined}
        onConnectBank={!isBankConnected ? handleTransferToBankConnectBank : undefined}
        telemetry={{
          onConnectBankViewViewed: adminRegistryTelemetry.transferDialogPromoViewed,
          onPendingViewViewed: () => telemetry.payoutDialog.enter('NotStarted'),
          onTransferBalanceClicked: telemetry.transferBalanceButtonClicked,
          onNevermindClicked: telemetry.nevermindButtonClicked,
          onStatusCloseClicked: telemetry.closePayoutDialogStatusButtonClicked,
          onAllyOpenAccountClicked: adminRegistryTelemetry.transferDialogPromoOpenAccountClicked,
        }}
        payoutFormProps={
          isBankConnected
            ? {
                isPayoutAllowed: payoutAccountAllowed,
                withdrawBalance,
                loadingPayoutRequest,
                accountNumber,
              }
            : undefined
        }
        payoutStatus={isBankConnected ? payoutStatus : undefined}
      />
      {payoutAccountInfo && (
        <PayoutDialog
          isOpen={isOpenPayoutDialog}
          onClose={onClosePayoutDialog}
          isPayoutAllowed={payoutAccountAllowed}
          availableBalance={payoutAccountInfo.balance?.available}
          withdrawBalance={withdrawBalance}
          fetchPayoutInfo={fetchPayoutInfo}
          status={payoutStatus}
          loadingPayoutRequest={loadingPayoutRequest}
          accountNumber={accountNumber}
        />
      )}
    </Provider>
  );
};

export { CreditCardSetupProvider };
